import React, { useMemo, useEffect, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Typography,
  Grid,
  makeStyles,
  CircularProgress,
  Box,
} from '@material-ui/core';
import {
  DefaultButton,
  ErrorLabel,
  LottieAnimation,
  colors,
  misc,
} from 'design-system';

import {
  FINAL_EMISSION_STEP,
  trackEvent,
  trackCurrentStep,
  END_DATE,
  NEW_VEHICLE,
  COLLECTIVE_NAME,
  VALUE,
} from 'src/analytics';

import { Quotation } from 'data-layer-cr/dist/models';
import { useActivePdfDownloader } from 'data-layer-cr/dist/network';
import { getLoggedRole } from 'data-layer-cr/dist/authentication';
import { appliesAutoPxForPayment } from 'data-layer-cr/dist/functions/quotations';

import {
  creditCard,
  broker,
  cashPayment,
  achPayment,
  clavePayment,
} from '../../../../constants/paymentTypes';

// Network
import PdfClient from '../../../../services/PdfClient';

// Redux Related
import { RootState } from '../../../../store/rootReducer';
import { setStartedEmission } from '../../../../store/steppers/actions';

// Lottie File
import success from '../../../../lottieFiles/success.json';

const useStyles = makeStyles({
  image: {
    marginTop: -40,
  },
  topOffsetText: {
    marginTop: -40,
  },
  content: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  buttonContainer: {
    width: 300,
    display: 'inline-block',
  },
  titleText: {
    fontSize: 23,
    fontWeight: 900,
  },
  subTitleText: {
    fontSize: 17,
    lineHeight: '30px',
    maxWidth: 330,
    fontWeight: 700,
  },
  fontSubtitle: {
    fontSize: 15,
    lineHeight: '22px',
    fontWeight: 400,
    letterSpacing: '.1px',
    color: colors.abbey,
  },
  fontDescription: {
    fontSize: 13,
    lineHeight: '20px',
    fontWeight: 500,
    letterSpacing: '.1px',
    color: colors.abbey,
  },
  whatsappButton: {
    color: colors.bright_turquoise,
    fontWeight: 700,
    verticalAlign: 'bottom',
    '&:hover': {
      cursor: 'pointer',
    },
  },
});

/**
 * Generates a ReactNode with the provided confirmation message, based on the
 * selected payment.
 *
 * @param paymentType - The detected payment type
 * @param classes - jsx that define the page's styles
 */
function makeConfirmationMessage(
  paymentType: string,
  classes: ReturnType<typeof useStyles>,
  quotation: Quotation,
  orderId: string,
): React.ReactNode {
  const defaultSuccessText: React.ReactNode = (
    <Typography
      data-testid="default-success-text"
      variant="caption"
      align="center"
      className={classes.subTitleText}
    >
      Tu póliza ha sido emitida. Pronto estarás recibiendo un correo con tu
      póliza.
    </Typography>
  );

  if (appliesAutoPxForPayment(quotation, paymentType)) {
    return (
      <Typography
        variant="caption"
        align="center"
        className={classes.subTitleText}
        data-testid="text-auto-px"
      >
        Tu{' '}
        <b>
          póliza Auto<span style={{ color: colors.bright_turquoise }}>PX </span>
          ha sido emitida.{' '}
        </b>
        Pronto estarás recibiendo un correo con tu póliza.
      </Typography>
    );
  }

  if (orderId !== '') {
    return defaultSuccessText;
  }

  switch (paymentType) {
    case creditCard:
    case clavePayment:
      return defaultSuccessText;
    case broker:
      return (
        <Typography
          data-testid="text-AGE"
          variant="caption"
          align="center"
          className={classes.subTitleText}
        >
          Hemos enviado una <b>notificación al corredor</b> para que realice el
          pago y emisión de la póliza.
        </Typography>
      );
    case achPayment:
    case cashPayment:
      return (
        <Typography
          data-testid="text-DCW-ACH"
          variant="caption"
          align="center"
          className={classes.subTitleText}
        >
          Hemos enviado una <b>notificación a tu correo electrónico</b>.
        </Typography>
      );
    default:
      return defaultSuccessText;
  }
}

export const getPdfUrl = (orderId: string, quotationHash: string): string =>
  orderId !== ''
    ? `policyEmission/${orderId}`
    : `policyEmission/download/${quotationHash}`;

export const Confirmation = ({
  errorCode,
  paymentType,
  quotation,
  setStartedEmissionAction,
  orderId = '',
}: Props): JSX.Element => {
  const classes = useStyles();
  const url = getPdfUrl(orderId, quotation.urlSalt);
  const isValidDownloadPolicy = useMemo(() => {
    const ASE = 'ASE';
    const AGE = 'AGE';
    const LOG = getLoggedRole();
    return (
      paymentType === creditCard ||
      paymentType === clavePayment ||
      ([ASE, AGE].includes(LOG) && paymentType === broker) ||
      ([achPayment, cashPayment].includes(paymentType) && LOG === ASE) ||
      orderId !== ''
    );
  }, [paymentType, orderId]);
  const confirmationMessage = useMemo(
    () => makeConfirmationMessage(paymentType, classes, quotation, orderId),
    [paymentType, classes, quotation, orderId],
  );

  /**
   * Registers the active pdf downloader hook
   */
  const { requestPDfDownload, requestStatus, pdfFile } = useActivePdfDownloader(
    {
      requestParameters: {
        method: 'GET',
        url,
        client: PdfClient,
      },
    },
  );

  /**
   * Downloads the pdf to the client's machine
   */
  const downloadPdf = useCallback(() => {
    const pdfUrl = URL.createObjectURL(pdfFile as Blob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('target', '_blank');
    link.setAttribute('download', 'poliza.pdf');
    document.body.appendChild(link);
    link.click();
  }, [pdfFile]);

  const onPdfDownloadButtonClicked = useCallback(() => {
    if (typeof pdfFile !== 'undefined') {
      downloadPdf();
    } else {
      requestPDfDownload();
    }
  }, [pdfFile, requestPDfDownload, downloadPdf]);

  useEffect(() => {
    if (errorCode === '') {
      misc.confetti();
      trackEvent({
        eventName: FINAL_EMISSION_STEP,
        eventProperties: {
          [END_DATE]: new Date().toLocaleString(),
          [VALUE]: quotation.units[0].unit.value.toString(),
          [NEW_VEHICLE]: quotation.units[0].unit.isNewCar.toString(),
          [COLLECTIVE_NAME]: quotation.quotationProfile.description,
        },
      });
      trackCurrentStep(FINAL_EMISSION_STEP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof pdfFile !== 'undefined') {
      downloadPdf();
    }
  }, [pdfFile, downloadPdf]);

  useEffect(() => {
    setStartedEmissionAction(errorCode === '');
  }, [errorCode, setStartedEmissionAction]);

  if (errorCode !== '') {
    return (
      <Grid
        data-testid="error-layout"
        container
        spacing={4}
        className={classes.content}
      >
        <Grid item xs={12} container justifyContent="center">
          <Typography
            variant="body1"
            align="center"
            className={classes.titleText}
          >
            Lo sentimos, la transacción no pudo ser realizada
          </Typography>
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          <img alt="error" src="/images/error_image.svg" />
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          <Grid container justifyContent="center">
            <Typography align="center" className={classes.fontSubtitle}>
              Puedes intentar nuevamente. Si el error persiste, escríbenos al{' '}
              <Box
                component="span"
                className={classes.whatsappButton}
                onClick={(): Window | null =>
                  window.open(`https://wa.me/50625032772`)
                }
                id="sales-web-inspection-whatsapp-button"
                data-testid="whatsapp-button"
              >
                WhatsApp (506)2503-2772
              </Box>{' '}
              o a soporte técnico:
            </Typography>
            <a
              data-testid="anchor-email"
              href="mailto:soporteventasautocr@assanet.com"
              className={classes.fontSubtitle}
            >
              soporteventasautocr@assanet.com
            </a>
          </Grid>

          <Grid item xs={8}>
            <DefaultButton variant="outlined" fullWidth>
              Reintentar
            </DefaultButton>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <div className={classes.content}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="body2"
            align="center"
            className={classes.titleText}
          >
            ¡Gracias por elegirnos!
          </Typography>
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          <LottieAnimation animationJson={success} />
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          {confirmationMessage}
        </Grid>
        {isValidDownloadPolicy && (
          <>
            <Grid
              data-testid="download-pdf-container"
              item
              xs={12}
              container
              justifyContent="center"
            >
              <div className={classes.buttonContainer}>
                <DefaultButton
                  id="sales-web-inspection-button-downloadpolicy"
                  data-testid="download-button"
                  fullWidth
                  onClick={onPdfDownloadButtonClicked}
                >
                  {requestStatus === 'requesting' ? (
                    <CircularProgress data-testid="network-ind-pdf" size={20} />
                  ) : (
                    <>Descargar Póliza</>
                  )}
                </DefaultButton>
              </div>
            </Grid>
            {requestStatus === 'failure' && (
              <Grid item xs={12} container justifyContent="center">
                <ErrorLabel data-testid="download-error">
                  No se pudo obtener el pdf, por favor intente de nuevo
                </ErrorLabel>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </div>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  errorCode: string;
  quotation: Quotation;
  paymentType: string;
} => ({
  errorCode: state.emissionPolicy.emissionErrorCode,
  quotation: state.quotation.quotation,
  paymentType: state.emissionPolicy.paymentFields.type,
});
const reduxActions = {
  setStartedEmissionAction: setStartedEmission,
};

const connector = connect(mapStateToProps, reduxActions);

interface Props extends PropsFromRedux {
  quotation: Quotation;
  orderId?: string;
}

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Confirmation);
